import { ConfigValue } from '@ecosystem/newron-client-lib';
import { RouteManager } from '@ecosystem/newron-client-routes';
import { isEmpty, isEqual, isObject } from 'lodash';
import pluralize from 'pluralize';

import {
  ConfigItemDescriptor,
  KnownConfigItemType,
  MultiEditConfigItemType,
} from '../../../public/MultiEditRegister/types';
import {
  CollectionInsight,
  CollectionType,
  InsightErrorType,
  Proposition,
  UpdatedInsight,
} from '../../../shared/types';
import { mapPropositionToSlug } from '../Insight/utils';

import { Subtitle, WizardData } from './types';

const configItemDescriptorMap: Record<KnownConfigItemType, ConfigItemDescriptor> = {
  [KnownConfigItemType.ProductGroup]: {
    title: 'Product groups and countries',
    label: 'market',
  },
  [KnownConfigItemType.TimeFrame]: {
    title: 'Time frame',
  },
};

export const getMultiEditConfigItemType = (type: KnownConfigItemType): MultiEditConfigItemType | null => {
  const configItemDescriptor = configItemDescriptorMap[type];
  if (!configItemDescriptor) {
    return null;
  }
  return {
    type,
    ...configItemDescriptor,
  };
};

export const ReviewErrorDescription: Record<string, string> = {
  OriginalTags: 'Your changes will be applied, but not all selections could be loaded.',
  NewTags: 'Your latest selections will be applied, but some previous selections could not be loaded.',
  NoTags: 'Your changes will be applied, but we were not able to load any selections for this insight.',
  PartialTags: 'Only partial selections are available. All changes will still be applied.',
  InsightFailure: "Changes couldn't be applied due to an error. This insight remains unchanged.",
  UnexpectedPropositionError: 'An unexpected error occurred while loading the data.',
  InsightVisualizationError: 'Something went wrong while loading the insight, but your changes will be applied.',
  InsightSubscriptionError:
    'This insight is not part of your subscription plan. Any changes you make will not be saved.',
  InsightExpiredError:
    'This insight has expired and cannot be viewed or edited. Any changes you make will not be saved.',
  MultiConfigError: "Changes couldn't be applied due to an error.",
  DataNotAvailable: 'Data for the selected combination is not available.',
  None: '',
} as const;

export const mapErrorTypeToDescription: Record<InsightErrorType, keyof typeof ReviewErrorDescription> = {
  [InsightErrorType.OTHER]: ReviewErrorDescription.UnexpectedPropositionError,
  [InsightErrorType.RENDER_ERROR]: ReviewErrorDescription.InsightVisualizationError,
  [InsightErrorType.SUBSCRIPTION_ERROR]: ReviewErrorDescription.InsightSubscriptionError,
  [InsightErrorType.EXPIRED]: ReviewErrorDescription.InsightExpiredError,
  [InsightErrorType.BFF_ERROR]: ReviewErrorDescription.InsightFailure,
  [InsightErrorType.MULTI_CONFIG_ERROR]: ReviewErrorDescription.MultiConfigError,
  [InsightErrorType.DATA_NOT_AVAILABLE]: ReviewErrorDescription.DataNotAvailable,
};

// TODO: extend to be able to remove duplicates in objects too
export const getUniqueValues = <T>(values: T[], getKey?: (item: T) => unknown): T[] => {
  if (!getKey) {
    return Array.from(new Set(values));
  }
  const seen = new Set();
  return values.filter(item => {
    const key = getKey(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const buildAppliedConfigValues = (appliedValues: string[], label: string): string =>
  appliedValues.length === 0
    ? 'None'
    : appliedValues.length === 1
      ? appliedValues[0]
      : `${appliedValues.length} different ${pluralize(label, appliedValues.length)}`;

export const capitalize = (str: string): string => str?.charAt(0)?.toUpperCase() + str?.slice(1);

export const getPropositionLabel = (proposition: Proposition): string => {
  const slug = mapPropositionToSlug(proposition);
  return capitalize(RouteManager.getInstance().getRoute(slug)?.label ?? slug);
};

export const buildTitle = (propositionName: string, insightsCount: number): string => {
  return `${insightsCount} ${propositionName} ${pluralize('insight', insightsCount)}`;
};

export const buildReviewPageSubtitle = (
  collectionInsights: CollectionInsight<unknown>[],
  updatedInsights: UpdatedInsight<unknown>[],
  collectionName: string,
  collectionType: CollectionType
): Subtitle[] => {
  const editedInsightsCount = updatedInsights?.length || 0;
  const nonTextBlockInsights = collectionInsights.filter(insight => insight.proposition !== Proposition.Platform);
  const uneditedInsightsCount = nonTextBlockInsights.length - editedInsightsCount;
  const failedInsights = updatedInsights?.filter(insight => insight.hasPropositionError).length;

  const subtitleParts: Subtitle[] = [
    {
      title: `in "${collectionName}" ${collectionType}`,
    },
  ];

  if (uneditedInsightsCount > 0) {
    subtitleParts.push({ title: `${uneditedInsightsCount} unedited insights are hidden` });
  }

  if (failedInsights > 0 && failedInsights < editedInsightsCount) {
    subtitleParts.push({
      title: `${failedInsights} ${pluralize('insight', failedInsights)} failed to load`,
      error: true,
    });
  }

  return subtitleParts;
};

export const getErrorDescription = ({
  hasPropositionError,
  errorType,
  failedToLoadOriginalTags,
  failedToLoadNewTags,
}: {
  hasPropositionError?: boolean;
  errorType?: InsightErrorType;
  failedToLoadOriginalTags?: boolean;
  failedToLoadNewTags?: boolean;
}): string => {
  if (hasPropositionError) {
    return ReviewErrorDescription.InsightFailure;
  }

  if (!hasPropositionError && errorType) {
    return mapErrorTypeToDescription[errorType];
  }

  if (failedToLoadOriginalTags && failedToLoadNewTags) {
    return ReviewErrorDescription.NoTags;
  }

  if (failedToLoadOriginalTags) {
    return ReviewErrorDescription.NewTags;
  }

  if (failedToLoadNewTags) {
    return ReviewErrorDescription.OriginalTags;
  }

  return ReviewErrorDescription.None;
};

export const deepCompareIgnoreOrder = <T>(left: T, right: T): boolean => {
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }
    return left.every(item1 => right.some(item2 => isEqual(item1, item2)));
  }

  if (isObject(left) && isObject(right)) {
    const keysOfLeft = Object.keys(left);
    const keysOfRight = Object.keys(right);

    if (keysOfLeft.length !== keysOfRight.length) {
      return false;
    }

    return keysOfLeft.every(key => deepCompareIgnoreOrder(left[key], right[key]));
  }

  return isEqual(left, right);
};

export const doPropositionConfigValuesMatch = (
  appliedValues: ConfigValue,
  existingValuesPerInsight: ConfigValue[]
): boolean =>
  appliedValues &&
  existingValuesPerInsight.length > 0 &&
  existingValuesPerInsight.every(existingValues => deepCompareIgnoreOrder(appliedValues, existingValues));

export const doAllConfigValuesMatch = (
  appliedConfigValues: WizardData['appliedConfigValues'],
  existingConfigInfos: WizardData['configItemInfos']
): boolean => {
  return Object.entries(appliedConfigValues).every(([proposition, appliedValues]) => {
    const existing = existingConfigInfos[proposition]?.configValues;

    // If both existing and appliedValues are empty, they match
    if (!existing && !appliedValues) {
      return true;
    }

    // If existing is non-empty, check the match
    return existing && !isEmpty(existing)
      ? appliedValues
        ? doPropositionConfigValuesMatch(appliedValues, Object.values(existing || {}))
        : true
      : false;
  });
};
